/**
 * Verkaufs-Timing-Optimizer — pure business logic
 *
 * Alle Funktionen sind seiteneffektfrei und werden in Vitest-Unit-Tests
 * geprüft ohne React- oder DB-Setup.
 *
 * Steuer-Logik:
 *   Buchgewinn = Verkaufspreis − Restbuchwert
 *   Positiver Buchgewinn → steuerpflichtiger Erlös
 *   Negativer Buchgewinn (= Buchverlust) → steuerlich günstiger
 *   Optimal = Restbuchwert ≈ Marktwert (Differenz nahe 0)
 *
 * Issue #580
 */

// ── Typen ────────────────────────────────────────────────────────────────────

export interface AfaInput {
  kaufpreis_netto: number;
  afa_beginn: string;           // ISO date string YYYY-MM-DD
  afa_nutzungsdauer_jahre: number;
}

export interface AfaVerlaufPunkt {
  datum: string;                // ISO date string
  restbuchwert: number;
}

export interface MarktwertPunkt {
  datum: string;                // ISO date string
  marktwert: number;
}

export interface SchnittpunktErgebnis {
  datum: string;
  restbuchwert: number;
  marktwert: number;
}

export interface VerkaufsSzenario {
  datum: string;
  restbuchwert: number;
  marktwert: number | null;
  buchgewinn: number | null;    // positiv = Buchgewinn, negativ = Buchverlust
}

export interface SzenarienErgebnis {
  jetzt: VerkaufsSzenario;
  optimal: VerkaufsSzenario | null;
  vollabschreibung: VerkaufsSzenario;
}

export interface BannerZustand {
  bannerA: boolean;   // Restbuchwert fällt in < 6 Monaten unter 30 % des Marktwerts
  bannerB: boolean;   // Vollabschreibung in < 12 Monaten
}

// ── Interne Hilfsfunktionen ──────────────────────────────────────────────────

function parseDate(iso: string): Date {
  return new Date(iso + 'T00:00:00Z');
}

function addJahre(date: Date, jahre: number): Date {
  const d = new Date(date);
  d.setUTCFullYear(d.getUTCFullYear() + jahre);
  return d;
}

function addMonate(date: Date, monate: number): Date {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + monate);
  return d;
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function tageDiff(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
}

// ── Öffentliche Funktionen ───────────────────────────────────────────────────

/**
 * Berechnet den linearen AfA-Restbuchwert an einem bestimmten Stichtag.
 * Gibt kaufpreis_netto zurück wenn afa_beginn in der Zukunft liegt.
 * Gibt 0 zurück wenn das Fahrzeug bereits vollständig abgeschrieben ist.
 */
export function berechneRestbuchwert(input: AfaInput, stichtag: string): number {
  const beginn = parseDate(input.afa_beginn);
  const tag = parseDate(stichtag);
  const vollabschreibung = addJahre(beginn, input.afa_nutzungsdauer_jahre);

  if (tag <= beginn) return input.kaufpreis_netto;
  if (tag >= vollabschreibung) return 0;

  const gesamtTage = tageDiff(beginn, vollabschreibung);
  const vergangene = tageDiff(beginn, tag);
  const abschreibungsrate = input.kaufpreis_netto / gesamtTage;
  const restbuchwert = input.kaufpreis_netto - abschreibungsrate * vergangene;
  return Math.max(0, restbuchwert);
}

/**
 * Gibt das Datum der Vollabschreibung zurück (afa_beginn + nutzungsdauer).
 */
export function berechneVollabschreibungsDatum(input: AfaInput): string {
  const beginn = parseDate(input.afa_beginn);
  return toIso(addJahre(beginn, input.afa_nutzungsdauer_jahre));
}

/**
 * Erzeugt monatliche Datenpunkte des AfA-Verlaufs von afa_beginn bis zur
 * Vollabschreibung. Startpunkt = kaufpreis_netto, Endpunkt = 0.
 */
export function berechneAfaVerlauf(input: AfaInput): AfaVerlaufPunkt[] {
  const beginn = parseDate(input.afa_beginn);
  const vollabschreibung = addJahre(beginn, input.afa_nutzungsdauer_jahre);
  const punkte: AfaVerlaufPunkt[] = [];

  let current = new Date(beginn);
  while (current <= vollabschreibung) {
    punkte.push({
      datum: toIso(current),
      restbuchwert: berechneRestbuchwert(input, toIso(current)),
    });
    current = addMonate(current, 1);
  }

  // Sicherstellen dass der letzte Punkt genau das Vollabschreibungsdatum ist
  const lastDatum = toIso(vollabschreibung);
  if (punkte.length === 0 || punkte[punkte.length - 1].datum !== lastDatum) {
    punkte.push({ datum: lastDatum, restbuchwert: 0 });
  } else {
    punkte[punkte.length - 1].restbuchwert = 0;
  }

  return punkte;
}

/**
 * Interpoliert den Marktwert an einem Datum zwischen zwei bekannten Punkten.
 * Lineare Interpolation.
 */
function interpoliereMarktwert(
  datum: Date,
  p1: MarktwertPunkt,
  p2: MarktwertPunkt,
): number {
  const t1 = parseDate(p1.datum).getTime();
  const t2 = parseDate(p2.datum).getTime();
  const t = datum.getTime();
  if (t2 === t1) return p1.marktwert;
  const frac = (t - t1) / (t2 - t1);
  return p1.marktwert + frac * (p2.marktwert - p1.marktwert);
}

/**
 * Findet den optimalen Verkaufszeitpunkt = Schnittpunkt der Marktwert-Kurve
 * mit der AfA-Linie. Gibt null zurück wenn kein Schnittpunkt existiert oder
 * keine Marktwert-Daten vorhanden sind.
 */
export function berechneOptimalerVerkaufszeitpunkt(
  input: AfaInput,
  marktwerte: MarktwertPunkt[],
): SchnittpunktErgebnis | null {
  if (marktwerte.length === 0) return null;

  const sorted = [...marktwerte].sort(
    (a, b) => parseDate(a.datum).getTime() - parseDate(b.datum).getTime(),
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const p1 = sorted[i];
    const p2 = sorted[i + 1];
    const d1 = parseDate(p1.datum);
    const d2 = parseDate(p2.datum);

    const rbw1 = berechneRestbuchwert(input, p1.datum);
    const rbw2 = berechneRestbuchwert(input, p2.datum);

    // Schnittpunkt: (Marktwert - Restbuchwert) wechselt das Vorzeichen
    const diff1 = p1.marktwert - rbw1;
    const diff2 = p2.marktwert - rbw2;

    if (diff1 * diff2 <= 0) {
      // Lineare Interpolation des Schnittpunkts
      if (diff1 === diff2) continue;
      const frac = Math.abs(diff1) / (Math.abs(diff1) + Math.abs(diff2));
      const tMs = d1.getTime() + frac * (d2.getTime() - d1.getTime());
      const schnittDatum = toIso(new Date(tMs));
      const schnittRbw = berechneRestbuchwert(input, schnittDatum);
      const schnittMw = interpoliereMarktwert(new Date(tMs), p1, p2);
      return { datum: schnittDatum, restbuchwert: schnittRbw, marktwert: schnittMw };
    }
  }

  return null;
}

/**
 * Berechnet alle drei Szenarien: jetzt, optimal (Schnittpunkt), Vollabschreibung.
 * marktwertHeute kann null sein wenn kein Marktwert eingetragen.
 * marktwertPunkte: echte DB-Datenpunkte — wenn übergeben, werden sie für die
 * Optimal-Berechnung verwendet (gleiche Basis wie der Diagramm-Marker).
 * Ohne Datenpunkte fällt die Funktion auf eine synthetische 2-Punkte-Kurve zurück.
 */
export function berechneSzenarien(
  input: AfaInput,
  marktwertHeute: number | null,
  heute: string,
  marktwertPunkte?: MarktwertPunkt[],
): SzenarienErgebnis {
  const restbuchwertHeute = berechneRestbuchwert(input, heute);
  const buchgewinnHeute =
    marktwertHeute !== null ? marktwertHeute - restbuchwertHeute : null;

  const vollDatum = berechneVollabschreibungsDatum(input);
  // Schätzung: Marktwert bei Vollabschreibung ≈ 40 % des heutigen Werts (gleich dem synthetischen Modell)
  const vollMarktwert = marktwertHeute !== null ? marktwertHeute * 0.4 : null;

  const jetzt: VerkaufsSzenario = {
    datum: heute,
    restbuchwert: restbuchwertHeute,
    marktwert: marktwertHeute,
    buchgewinn: buchgewinnHeute,
  };

  const vollabschreibung: VerkaufsSzenario = {
    datum: vollDatum,
    restbuchwert: 0,
    marktwert: vollMarktwert,
    buchgewinn: vollMarktwert !== null ? vollMarktwert : null,
  };

  // Für optimal: echte DB-Punkte bevorzugen, sonst synthetische 2-Punkte-Kurve
  let optimal: VerkaufsSzenario | null = null;
  if (marktwertHeute !== null) {
    const punkte: MarktwertPunkt[] =
      marktwertPunkte && marktwertPunkte.length > 0
        ? marktwertPunkte
        : [
            { datum: heute, marktwert: marktwertHeute },
            { datum: vollDatum, marktwert: marktwertHeute * 0.4 },
          ];
    const schnittpunkt = berechneOptimalerVerkaufszeitpunkt(input, punkte);
    if (schnittpunkt) {
      optimal = {
        datum: schnittpunkt.datum,
        restbuchwert: schnittpunkt.restbuchwert,
        marktwert: schnittpunkt.marktwert,
        buchgewinn: schnittpunkt.marktwert - schnittpunkt.restbuchwert,
      };
    }
  }

  return { jetzt, optimal, vollabschreibung };
}

/**
 * Berechnet welche Warnbanner angezeigt werden sollen.
 *
 * Banner A: Restbuchwert fällt in < 6 Monaten unter 30 % des Marktwerts
 * Banner B: Vollabschreibung in < 12 Monaten
 */
export function berechneBannerZustand(
  input: AfaInput,
  marktwertHeute: number | null,
  heute: string,
): BannerZustand {
  const heuteDate = parseDate(heute);
  const vollDatum = parseDate(berechneVollabschreibungsDatum(input));

  const msBisVoll = vollDatum.getTime() - heuteDate.getTime();
  const monateBisVoll = msBisVoll / (1000 * 60 * 60 * 24 * 30.44);

  const bannerB = monateBisVoll > 0 && monateBisVoll < 12;

  let bannerA = false;
  if (marktwertHeute !== null && marktwertHeute > 0) {
    const in6Monaten = toIso(addMonate(heuteDate, 6));
    const rbwIn6Monaten = berechneRestbuchwert(input, in6Monaten);
    const schwelle30Prozent = marktwertHeute * 0.3;
    bannerA = rbwIn6Monaten < schwelle30Prozent && monateBisVoll > 0;
  }

  return { bannerA, bannerB };
}
